import React, { useEffect, useMemo, useRef } from "react"

import { useCreatorResolver } from "@/organization"
import {
  Card,
  CardContent,
  CopyableId,
  DrawerBody,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Skeleton,
} from "@coval/ui"

import { DetailRow, KeyValueChips } from "@/components/detail/DetailFields"
import { DrawerOpenInNewTabAction } from "@/components/drawer/DrawerOpenInNewTabAction"
import type { GetTestSetVersionsResponse } from "@/types_api/backend/transport/get_test_set_versions_response"
import type { TestSetRecord } from "@/types/api/TestSetRecord"
import { asJsonFieldValueOrNull, asJsonObjectValueOrNull } from "@/types/json"
import { formatRelativeTimestamp } from "@/utils/formatting/format-timestamp"

import {
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
  useTestSetVersionsTransport,
} from "../transport"
import {
  hasTestCaseDetails,
  TestCaseDetails,
  type TestCaseData,
} from "./TestCaseDetails"

type TestSetVersion = GetTestSetVersionsResponse["versions"][number]
type TestCaseCardData = TestCaseData & { id: string }

interface TestSetDrawerPanelProps {
  testSetId: string
  selectedTestCaseId?: string
  /**
   * The test case as the conversation that opened this drawer actually ran it.
   * The test set's own rows are fetched live and carry neither the run's
   * metadata nor the wording the case had at run time, so the snapshot is shown
   * alongside them rather than left to be inferred from the current test set.
   */
  runTestCase?: TestCaseData | null
  testSetVersionId?: string
  /**
   * Params the run that opened this drawer actually executed the selected
   * test case with. Overrides that one card's stored params — the test
   * case's own params can vary run to run when it's parameterized.
   */
  executedTestCaseParams?: Record<string, string>
}

function toTestCaseCardData(
  raw: Record<string, unknown>,
  index: number,
): TestCaseCardData {
  return {
    id: typeof raw.id === "string" ? raw.id : `test-case-${index}`,
    description:
      typeof raw.description === "string" ? raw.description : undefined,
    inputStr: typeof raw.input_str === "string" ? raw.input_str : undefined,
    expectedBehaviors: Array.isArray(raw.expected_behaviors)
      ? raw.expected_behaviors.filter(
          (behavior): behavior is string => typeof behavior === "string",
        )
      : [],
    expectedOutputJson:
      asJsonFieldValueOrNull(raw.expected_output_json) ?? undefined,
    simulationMetadataInput: asJsonObjectValueOrNull(
      raw.simulation_metadata_input,
    ),
    metricInput: asJsonFieldValueOrNull(raw.metric_input) ?? undefined,
  }
}

function TestCaseCard({
  testCase,
  index,
  selected,
  cardRef,
}: {
  testCase: TestCaseCardData
  index: number
  selected: boolean
  cardRef: React.Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={cardRef}
      data-testid={`test-case-card-${testCase.id}`}
      tabIndex={selected ? -1 : undefined}
      className="rounded-lg border border-coval-border bg-surface-default p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-normal text-coval-text-muted">
          Test Case {index + 1}
        </span>
        <CopyableId id={testCase.id} label="Copy test case ID" />
      </div>

      <TestCaseDetails testCase={testCase} />
    </div>
  )
}

function TestSetDrawerContent({
  testSet,
  testSetVersion,
}: {
  testSet: TestSetRecord
  testSetVersion?: TestSetVersion
}) {
  const parameters = testSetVersion
    ? testSetVersion.parameters
    : testSet.parameters
  const metadata = testSetVersion
    ? testSetVersion.test_set_metadata
    : testSet.test_set_metadata
  const createdAt = testSetVersion
    ? testSetVersion.created_at
    : testSet.created_at
  const createdBy = testSetVersion
    ? testSetVersion.created_by
    : testSet.created_by_id
  const parameterEntries =
    parameters && !Array.isArray(parameters) ? Object.entries(parameters) : null
  const legacyParameterValues = Array.isArray(parameters) ? parameters : null
  const { resolveCreator } = useCreatorResolver()
  return (
    <Card>
      <CardContent className="pt-4">
        <dl className="space-y-5">
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            <DetailRow label={testSetVersion ? "Version created" : "Created"}>
              {createdAt
                ? new Date(createdAt).toLocaleString(undefined, {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })
                : "—"}
            </DetailRow>
            <DetailRow
              label={testSetVersion ? "Version created by" : "Created by"}
            >
              {resolveCreator(createdBy)}
            </DetailRow>
            {!testSetVersion ? (
              <DetailRow label="Last edited">
                {testSet.last_updated_at
                  ? formatRelativeTimestamp(testSet.last_updated_at)
                  : "—"}
              </DetailRow>
            ) : null}
          </div>
          {typeof metadata?.description === "string" && metadata.description ? (
            <DetailRow label="Description">
              <p className="whitespace-pre-wrap">{metadata.description}</p>
            </DetailRow>
          ) : null}
          {(parameterEntries && parameterEntries.length > 0) ||
          (legacyParameterValues && legacyParameterValues.length > 0) ? (
            <DetailRow label="Parameters">
              {parameterEntries ? (
                <KeyValueChips entries={parameterEntries} />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {legacyParameterValues?.map((param, index) => (
                    <span
                      key={
                        typeof param === "string"
                          ? `${index}:${param}`
                          : `${index}:${JSON.stringify(param)}`
                      }
                      className="inline-flex items-center rounded-md bg-surface-1 px-2 py-0.5 text-xs text-coval-text"
                    >
                      {typeof param === "string"
                        ? param
                        : JSON.stringify(param)}
                    </span>
                  ))}
                </div>
              )}
            </DetailRow>
          ) : null}
          {testSet.tags && testSet.tags.length > 0 ? (
            <DetailRow label="Tags">
              <div className="flex flex-wrap gap-1.5">
                {testSet.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-md bg-surface-1 px-2 py-0.5 text-xs text-coval-text"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </DetailRow>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * One test set's read-only detail view, as the body of a drawer the caller owns.
 * The caller supplies the drawer so a single panel can swap between resources
 * instead of stacking one drawer per kind.
 */
export const TestSetDrawerPanel: React.FC<TestSetDrawerPanelProps> = ({
  testSetId,
  selectedTestCaseId,
  runTestCase,
  testSetVersionId,
  executedTestCaseParams,
}) => {
  const { data: metadataData, isLoading: isMetadataLoading } =
    useTestSetMetadataTransport(testSetId)
  const { data: testCases, isLoading: isTestCasesLoading } =
    useTestSetRecordsTransport(
      { test_set_id: testSetId },
      { enabled: !testSetVersionId },
    )
  const versionsQuery = useTestSetVersionsTransport(testSetId, {
    enabled: Boolean(testSetVersionId),
  })
  const testCaseCardRefs = useRef(new Map<string, HTMLDivElement>())
  const testSet = metadataData?.metadata ?? null
  const testSetVersion = testSetVersionId
    ? versionsQuery.data?.versions.find(
        (version) => version.ulid === testSetVersionId,
      )
    : undefined
  const displayedTestCases = useMemo(
    () =>
      (testSetVersionId ? testSetVersion?.test_cases : testCases)?.map(
        (testCase, index) => {
          const card = toTestCaseCardData(
            testCase as unknown as Record<string, unknown>,
            index,
          )
          // The selected card is the one the opening run executed against —
          // show the params it actually ran with, not the case's current
          // stored ones, which can differ for a parameterized test case.
          if (executedTestCaseParams && card.id === selectedTestCaseId) {
            return { ...card, testCaseParams: executedTestCaseParams }
          }
          return card
        },
      ),
    [
      testCases,
      testSetVersion,
      testSetVersionId,
      executedTestCaseParams,
      selectedTestCaseId,
    ],
  )
  const isLoading =
    isMetadataLoading ||
    (testSetVersionId ? versionsQuery.isLoading : isTestCasesLoading)
  const isVersionUnavailable =
    Boolean(testSetVersionId) &&
    !isLoading &&
    (versionsQuery.isError || !testSetVersion)
  useEffect(() => {
    if (isLoading || !selectedTestCaseId || !displayedTestCases) {
      return
    }
    const card = testCaseCardRefs.current.get(selectedTestCaseId)
    card?.scrollIntoView({ block: "nearest" })
    card?.focus()
  }, [displayedTestCases, isLoading, selectedTestCaseId])

  // A run pins the version it ran, and the drawer shows that version, but the
  // test set only has one editable page and it holds the current version. The
  // link out is offered either way — a reader who came from a result is the one
  // most likely to want to edit the test set — and says which version it lands
  // on so following it is not mistaken for editing what ran.
  return (
    <>
      <DrawerHeader
        actions={
          <DrawerOpenInNewTabAction
            path={`/test-sets/${testSetId}`}
            label={
              testSetVersionId
                ? "Open current test set in new tab"
                : "Open test set in new tab"
            }
          />
        }
      >
        <DrawerTitle>
          {isMetadataLoading
            ? "Loading…"
            : (testSet?.display_name ?? "Test Set")}
        </DrawerTitle>
        {testSetVersionId || testSet ? (
          <DrawerDescription className="flex items-center gap-1">
            {testSetVersionId ? (
              <>
                <span>
                  {testSetVersion
                    ? `Version ${testSetVersion.version_number}${
                        testSetVersion.label ? ` · ${testSetVersion.label}` : ""
                      }`
                    : "Historical version"}
                </span>
                {testSet ? <span aria-hidden>·</span> : null}
              </>
            ) : null}
            {testSet ? (
              <>
                <span>ID:</span>
                <CopyableId id={testSet.id} label="Copy test set ID" />
              </>
            ) : null}
          </DrawerDescription>
        ) : null}
      </DrawerHeader>
      <DrawerBody>
        {runTestCase && hasTestCaseDetails(runTestCase) ? (
          <div className="space-y-3 pb-8">
            <h3 className="text-sm font-semibold text-coval-text">
              From this conversation
            </h3>
            <div className="rounded-lg border border-coval-border bg-surface-default p-4 space-y-3">
              <TestCaseDetails testCase={runTestCase} />
            </div>
          </div>
        ) : null}
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isVersionUnavailable ? (
          <p className="text-coval-text-muted text-sm">
            The test set version used by this conversation is no longer
            available.
          </p>
        ) : testSet ? (
          <div className="space-y-8 pt-2">
            <TestSetDrawerContent
              testSet={testSet}
              testSetVersion={testSetVersion}
            />
            {displayedTestCases && displayedTestCases.length > 0 ? (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-coval-text">
                  Test Cases ({displayedTestCases.length})
                </h3>
                {displayedTestCases.map((tc, i) => (
                  <TestCaseCard
                    key={tc.id}
                    testCase={tc}
                    index={i}
                    selected={tc.id === selectedTestCaseId}
                    cardRef={(node) => {
                      if (node) {
                        testCaseCardRefs.current.set(tc.id, node)
                      } else {
                        testCaseCardRefs.current.delete(tc.id)
                      }
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-coval-text-muted text-sm">Test set not found.</p>
        )}
      </DrawerBody>
    </>
  )
}
